import { Button } from "@/design-system/Button/Button";
import { CompletedIcon } from "@/designSystemIcons";
import { trackSendToExtension } from "@/events/sbcMixpanel";
import { CustomizeButton } from "@/pages/Sbcs/components/SolveSettings/CustomizeButton";
import { useTranslation } from "@repo/shared/i18n";
import React from "react";

/**
 * What the user can do with the squad on screen. Stacked in the desktop rail;
 * on phones one row, where the primary action keeps its words and the rest
 * become icons. Solving again lives inside Customize: its primary button
 * re-solves with whatever was changed. A control whose handler is left out is
 * not shown: the Streamlined SBC page has no complete button without the
 * extension.
 */
export const SolutionActions = ({
  isCompleted,
  isSolving,
  showSendToExtension,
  compact = false,
  extra,
  alternatives,
  onSolveAgain,
  onComplete,
  onUncomplete,
}: {
  isCompleted: boolean;
  isSolving: boolean;
  /** The extension can take the squad straight into the game. */
  showSendToExtension: boolean;
  /** One row with icon buttons, for phones. */
  compact?: boolean;
  /** An extra icon button for the compact row: the challenge's reward and requirements. */
  extra?: React.ReactNode;
  /** The alternatives control: a pill in the rail, an icon in the compact row. */
  alternatives?: React.ReactNode;
  onSolveAgain?: () => void;
  onComplete?: () => void;
  onUncomplete?: () => void;
}) => {
  const { t } = useTranslation("sbcSolution");
  const done = Boolean(isCompleted);

  const handleComplete = () => {
    if (done) return;
    if (showSendToExtension) trackSendToExtension();
    onComplete?.();
  };

  const handleSolve = () => {
    window.scrollTo(0, 0);
    onSolveAgain?.();
    if (isCompleted) onUncomplete?.();
  };

  const complete = onComplete && (
    <Button
      variant="primary"
      size="small"
      stretch
      disabled={done || isSolving}
      icon={done ? <CompletedIcon width="16" height="16" /> : undefined}
      onClick={handleComplete}
    >
      {showSendToExtension
        ? done
          ? t("sentToExtension")
          : t("sendToExtension")
        : done
          ? t("completed")
          : t("complete")}
    </Button>
  );

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {onSolveAgain && (
          <CustomizeButton
            compact
            disabled={isSolving}
            onApply={handleSolve}
            applyLabel={t("solveAgain")}
          />
        )}
        {extra}
        {alternatives}
        <div className="min-w-0 flex-1">{complete}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {complete}
      {alternatives}
      {onSolveAgain && (
        <CustomizeButton
          stretch
          placement="right"
          disabled={isSolving}
          onApply={handleSolve}
          applyLabel={t("solveAgain")}
        />
      )}
    </div>
  );
};
